import { captureException, init as initSentry, setTag } from "@sentry/browser";
import { GlobalWorkerOptions } from "pdfjs-dist";
import PDFWorker from "pdfjs-dist/build/pdf.worker.min?worker";
import { actionManager } from "@/actionManager/actionManager";
import { Animator } from "@/animations/Animator";
import { ACCOUNT_ONBOARDING_GET_V2, BOARD_POD_GET_V2 } from "@/api/apiEndpoints";
import { appInterface } from "@/appInterface";
import { canv } from "@/canv/global";
import { checkMaxNum, generateUID, isGuestToken, logException, MakeRequest } from "@/canv/utils";
import { canvasController } from "@/canvasController";
import {
    getNavigationModeLS,
    getViewport,
    replaceMindMaps,
    setViewWidgetsSettingsFromLS,
} from "@/canvasController/helpers";
import { REAL_HTML_PADDING_X, REAL_HTML_PADDING_Y, REAL_HTML_TOP_HEIGHT } from "@/constants";
import { ContextMenuController } from "@/contextMenuController";
import { CommentWidget } from "@/elements/comment";
import { CommentsPopovers } from "@/elements/comment/CommentsPopovers";
import { initEmojiFont } from "@/elements/emoji/utils";
import { TextEditingController } from "@/elements/text/textEditingController";
import { eventEmitter } from "@/eventEmitter";
import { EventManager } from "@/eventManager/eventManager";
import { DEFAULT_FONT_FAMILY, DEFAULT_FONT_REQUIREMENTS } from "@/fonts/constants";
import { ensureFontLoaded } from "@/fonts/fontRegistry";
import type { Group } from "@/group";
import { KeyboardHandler3 } from "@/helpers/keyboard";
import { rafThrottle } from "@/helpers/rafThrottle";
import { Intercom } from "@/intercom/intercom";
import { Mediator } from "@/mediator";
import { multiManager } from "@/multiManager/multiManager";
import { ProgressBar } from "@/progressBar";
import { searchController } from "@/searchController";
import { ShapeStorage } from "@/shapeStorage";
import { selectionManager } from "@/tools/cursorTool";
import { toolBox } from "@/tools/toolBox";
import { getScaledSize, isMobile } from "@/utils.js";
import { widgetMenu } from "@/widgetMenu/widgetMenu";
import type { BaseInfo, HoverArea, InitialData } from "./types";

export class canvClass {
    constructor() {
        this.canvas = document.getElementsByTagName("canvas")[0];

        if (this.canvas === undefined) {
            this.canvas = document.createElement("canvas");
        }

        this.ctx = this.canvas.getContext("2d") as CanvasRenderingContext2D;
        this.dpr = window.devicePixelRatio || 1;

        this.ctx.scale(this.dpr, this.dpr);
        this.m[0] = this.ctx.getTransform().a;
        this.m[3] = this.ctx.getTransform().a;

        this.adjustCanvas();

        this.ctx.lineJoin = "round";
        this.ctx.lineCap = "butt";

        this.progressBar = new ProgressBar();
        this.toolBox = new toolBox();
        this.keyboard = new KeyboardHandler3();
        this.contextMenu = new ContextMenuController();
        this.canvasController = new canvasController();
        this.appInterface = new appInterface();
        this.intercom = new Intercom();
        this.multiManager = new multiManager();
        this.animator = new Animator();
        this.mediator = new Mediator();
        this.widgetMenu = new widgetMenu();
        this.actionManager = new actionManager();
        this.eventManager = new EventManager();
        this.shapeStorage = new ShapeStorage();
        this.commentsPopovers = new CommentsPopovers();
        this.textEditingController = new TextEditingController();

        this.host = "https://doski.mts-link.ru";
        this.wsHost = "wss://doski.mts-link.ru";
        this.reserveWsHost = "wss://wsboard.mts-link.ru";
        this.requestRedraw = this.requestRedraw.bind(this);
    }

    adjustCanvas() {
        this.canvas.style.width = `${window.innerWidth}px`;
        this.canvas.style.height = `${window.innerHeight}px`;

        const rect = this.canvas.getBoundingClientRect();

        this.canvas.width = rect.width * this.dpr;
        this.canvas.height = rect.height * this.dpr;

        this.ctx.setTransform(this.m[0], 0, 0, this.m[3], this.m[4], this.m[5]);
        this.redraw();
    }

    async getPodAlias() {
        const requestData = { boardUID: canv.getInitDataProp("boardUID") };

        let res: Response | undefined;

        try {
            res = await fetch(canv.host + BOARD_POD_GET_V2, {
                method: "POST",
                body: JSON.stringify(requestData),
            });

            if (!res.ok) {
                throw 1;
            }
        } catch {
            throw 1;
        }

        const resJson = await res.json();

        if (resJson.status !== "success") {
            throw 1;
        }

        let podAlias = "1";
        if (resJson.pod !== undefined) {
            podAlias = resJson.pod;
        }
        if (resJson.reserve_pod !== undefined) {
            this.reservePodAlias = resJson.reserve_pod;
        }

        return podAlias;
    }

    async waitPodAlias(timeout: number) {
        const fn = async (resolve: (value: unknown) => void) => {
            try {
                const res = await this.getPodAlias();
                resolve(res);
            } catch {
                return setTimeout(() => resolve(this.waitPodAlias(timeout * 2)), timeout);
            }
        };

        return new Promise(fn);
    }

    async connect(timeout: number, mainTry = 0, reserveTry = 0, current = "main") {
        const query = canv.getInitDataProp("clientUID")
            ? `?clientUID=${canv.getInitDataProp("clientUID")}`
            : "";
        const path =
            current === "main"
                ? `${this.wsHost}/ws/${this.podAlias}${query}`
                : `${this.reserveWsHost}/ws/${this.reservePodAlias}${query}`;

        const fn = async (resolve: (value?: unknown) => void) => {
            try {
                await this.intercom.reConnect(path);
                resolve();
            } catch {
                let newMainTry = mainTry;
                let newReserveTry = reserveTry;
                let newCurrent = current;
                let newTimeout = timeout * 2;

                if (current === "main") {
                    newMainTry++;
                    if (newMainTry >= 5) {
                        newCurrent = "reserve";
                        newMainTry = 0;
                        newReserveTry = 0;
                        newTimeout = 1000;
                    }
                } else {
                    newReserveTry++;
                    if (newReserveTry >= 5) {
                        newCurrent = "main";
                        newReserveTry = 0;
                        newMainTry = 0;
                        newTimeout = 1000;
                    }
                }
                return setTimeout(
                    () => resolve(canv.connect(newTimeout, newMainTry, newReserveTry, newCurrent)),
                    timeout,
                );
            }
        };

        return new Promise(fn);
    }

    async connectAndLoadCanvas() {
        this.isReconnectionProcess = true;
        canv.intercom.disabled = true;

        clearTimeout(canv.pingTimer);

        canv.multiManager.resetMultiUsers();
        canv.eventManager.clear();

        // ждем pod увеличивая интервалы
        this.podAlias = (await this.waitPodAlias(1000)) as string;

        // Ждем подключение увеличивая интервалы.
        await this.connect(1000);

        // бесконечно ждем initResponse. Если failed, тут ждем, но показываем reloadPanel
        const initResp = await this.intercom.initMessage();

        // Вот в этот момент могут прилететь event, а у нас еще не загружен canvas
        // Нужно сделать буфер, который применять после loadFlatEnitites

        //@ts-ignore
        this.sessionUID = initResp.sessionUID;

        this.intercom.requestUsersMessage();
        // бесконечно ждем descResponse. Если failed, тут ждем, но показываем reloadPanel
        const boardStr = await this.intercom.requestDeskMessage();

        // нужно сделать до loadFlatEnitites, так как loadState textWidget может отправляться event
        //  в catch блоке выключаем обратно
        canv.intercom.disabled = false;

        GlobalWorkerOptions.workerPort = new PDFWorker();
        try {
            canv.canvasController.items.forEach((g) => {
                const child = g.onlyChild();

                if (child?.constructorName === "embed") {
                    child.beforeDeletion();
                }
            });

            canv.canvasController.items = [];
            canv.canvasController.groups = new Map();
            //await this.canvasController.loadCanv(boardStr);
            await this.canvasController.loadFlatEnitites(boardStr as string);
        } catch (e) {
            canv.intercom.disabled = true;
            // тут не возвращаем isReconnectionProcess, intercom.disabled и pinger. Потому-что уже просто просим перезагрузку
            logException(e);
            throw 1;
        }

        // Тут может быть ситуация, когда в процессе connectAndLoadCanvas срабатывает reloadPanel, а в конце connectAndLoadCanvas обратно включается canv.intercom.disabled = true
        // Можно сделать флажок canv.isAppBroken и смотреть на него в конце connectAndLoadCanvas

        this.isReconnectionProcess = false;
        canv.pingTimer = setTimeout(canv.ping.bind(canv), 60000);
        selectionManager.clearSelected();

        canv.redraw();
        canv.redraw(true);
    }

    async ping() {
        try {
            await this.intercom.pingMessage();

            this.pingTimer = setTimeout(this.ping.bind(this), 60000);
        } catch {
            this.appInterface.reconnectWindow.show();
        }
    }

    getInitDataProp<T extends keyof InitialData>(prop: T): InitialData[T] {
        if (this.initData === undefined) {
            throw new Error("Init data not loaded");
        }

        return this.initData?.[prop];
    }

    setInitDataProp<T extends keyof InitialData>(prop: T, value: InitialData[T]) {
        if (this.initData !== undefined) {
            this.initData[prop] = value;
        }

        eventEmitter.dispatch("board.info", {
            ...this.initData,
        });
    }

    async initApp(baseInfo: BaseInfo) {
        const navigationModeLS = getNavigationModeLS();
        const navigationMode =
            baseInfo.temporary && navigationModeLS ? navigationModeLS : baseInfo.navigationMode;

        this.initData = {
            userName: baseInfo.accountName,
            userEmail: baseInfo.login,
            userToken: baseInfo.token,
            fePrefix: baseInfo.fePrefix,
            boardUID: baseInfo.boardUID,
            boardName: baseInfo.boardName,
            boardMode: baseInfo.access,
            JWT: baseInfo.jwt,
            boardAccessToken: baseInfo.boardAccessToken,
            needPassword: baseInfo.needPassword,
            clientUID: baseInfo.clientUID !== undefined ? baseInfo.clientUID : "",
            isFavourite: baseInfo.isFavourite !== undefined ? baseInfo.isFavourite : false,
            canUseFavourite:
                baseInfo.canUseFavourite !== undefined ? baseInfo.canUseFavourite : false,
            isProfessional: baseInfo.isProfessional,
            isTeamOwner: baseInfo.isTeamOwner,
            isOnPrem: baseInfo.isOnPrem,
            teamId: baseInfo.teamId,
            onlyRead: baseInfo.onlyRead,
            isSSL: baseInfo.isSSL,
            appDomain: baseInfo.appDomain,
            isGuest: baseInfo.temporary !== undefined ? baseInfo.temporary : true,
            isTeamMember: baseInfo.isTeamMember,
            followTargetToken: baseInfo.followTargetToken,
            interfaceLanguage: baseInfo.interfaceLanguage,
            navigationMode: navigationMode,
            signature: baseInfo.signature !== undefined ? baseInfo.signature : "",
            accountID: baseInfo.accountID !== undefined ? baseInfo.accountID : "",
            temporary: baseInfo.temporary !== undefined ? baseInfo.temporary : true,
        };

        eventEmitter.dispatch("board.info", {
            ...this.initData,
        });

        this.actionManager.mode = baseInfo.navigationMode === "mouse" ? "mouse" : "trackpad";

        // default, проставляется в init response
        this.sessionUID = generateUID();

        this.setDomains(baseInfo);

        if (!this.initData.isOnPrem && process.env.NODE_ENV === "production") {
            this.initSentry();
        }
    }

    initSentry = () => {
        const getEnvironment = () => {
            const hostname = window.location.hostname;
            if (hostname.includes("stg")) {
                return "staging";
            }
            if (hostname.includes("dev")) {
                return "development";
            }
            return "production";
        };

        initSentry({
            dsn: "https://b2684a72220a67636dc2bbd9359e4741@sentry.webinar.ru/32",
            sendDefaultPii: true,
            environment: getEnvironment(),
            release: this.initData?.fePrefix,
        });

        if (this.initData?.userToken) {
            setTag("token", this.initData.userToken);
        }

        setTag("boardUID", this.initData?.boardUID);
    };

    /**
     * @param {Error | unknown} error error to capture
     * @returns {string} sentry event id
     */
    captureException = (error: unknown) => {
        return captureException(error);
    };

    setDomains(baseInfo: BaseInfo) {
        let HTTPDomain = "doski.mts-link.ru";

        if (baseInfo.appDomain !== undefined && baseInfo.appDomain !== "") {
            HTTPDomain = baseInfo.appDomain;
        }

        let httpScheme = "https://";
        if (baseInfo.isSSL !== undefined && !baseInfo.isSSL) {
            httpScheme = "http://";
        }

        this.host = httpScheme + HTTPDomain;

        let wsHost = "wss://wsboard.mts-link.ru/boards";
        if (baseInfo.wsDomain !== undefined && baseInfo.wsDomain !== "") {
            wsHost = baseInfo.wsDomain;
        }

        this.wsHost = wsHost;

        let reserveWsHost = "wss://wsboard.mts-link.ru";
        if (baseInfo.reserveWsDomain !== undefined && baseInfo.reserveWsDomain !== "") {
            reserveWsHost = baseInfo.reserveWsDomain;
        }
        this.reserveWsHost = reserveWsHost;
    }

    async tryPasswordToToken(baseInfo: BaseInfo) {
        const fn = async (resolve: (value?: unknown) => void) => {
            if (baseInfo.needPassword === undefined || baseInfo.needPassword === false) {
                resolve();
            } else {
                const success = (token: string) => {
                    canv.setInitDataProp("boardAccessToken", token);
                    resolve();
                };

                canv.appInterface.passwordWindow.open(success);
            }
        };

        return new Promise(fn);
    }

    async startApp(baseInfo: BaseInfo) {
        await this.initApp(baseInfo);
        await this.tryPasswordToToken(baseInfo);

        initEmojiFont();
        ensureFontLoaded(DEFAULT_FONT_FAMILY, DEFAULT_FONT_REQUIREMENTS);

        await canv.progressBar.show();
        canv.progressBar.moveTo(10);

        this.actionManager.initEventListeners();

        try {
            await this.connectAndLoadCanvas();
        } catch (e) {
            logException(e);
            return;
        }

        replaceMindMaps();

        if (canv.isBoardModeView() && !canv.isBoardModeViewCopy()) {
            this.toolBox.isDisabled = true;
        }

        canv.appInterface.updateTariffWindow.show(baseInfo.boardUID, baseInfo.onlyRead);

        canv.appInterface.show();
        this.multiManager.init();

        setViewWidgetsSettingsFromLS();

        this.updateZoomDisplay();

        if (this.canvasController.items.length !== 0) {
            this.zoomToSelected(this.canvasController.items);
        }

        if (this.isBoardModeFullRights()) {
            await this.actionManager.viewSaveLoad();

            if (this.canvasController.items.length !== 0) {
                if (!this.actionManager.hasItemInViewport()) {
                    this.zoomToSelected(this.canvasController.items);
                }
            }
        }
        this.checkLinkToWidget();

        canv.redraw();
        window.requestAnimationFrame(this.requestRedraw);
        await this.progressBar.hide();

        await this.runOnboarding();
    }

    async runOnboarding() {
        const params = new URLSearchParams(window.location.search);
        const followTargetToken = canv.getInitDataProp("followTargetToken");

        if (params.get("recordMode") === "true") {
            // hide onboarding
            return;
        }
        if (canv.getInitDataProp("temporary") && !isMobile() && !followTargetToken) {
            // если userToken нету, нету сохранненого navigation mode
            // очень актуально для интеграции
            // followTargetToken - это автоотслеживание, если он есть, то не нужно показывать модалку
            canv.appInterface.navigationSelectWindow.open();
            return;
        }

        const onboardingInfo = await MakeRequest(
            ACCOUNT_ONBOARDING_GET_V2,
            {
                token: canv.getInitDataProp("userToken"),
            },
            false,
            true,
        );

        if (isMobile()) {
            canv.actionManager.setTrackpadMode();
        } else if (onboardingInfo?.IsPassed === false) {
            canv.appInterface.navigationSelectWindow.open();
        }
    }

    isGrantedAccess() {
        return canv.getInitDataProp("boardAccessToken") !== "";
    }

    isBoardModeEditOrOwner() {
        return (
            canv.getInitDataProp("boardMode") === "edit" ||
            canv.getInitDataProp("boardMode") === "owner"
        );
    }

    isBoardModeOwner() {
        return canv.getInitDataProp("boardMode") === "owner";
    }

    isBoardModeView() {
        return (
            canv.getInitDataProp("boardMode") === "view" ||
            canv.getInitDataProp("boardMode") === "view_mini" ||
            canv.getInitDataProp("boardMode") === "view_copy"
        );
    }

    isBoardModeViewWithMini() {
        return (
            canv.getInitDataProp("boardMode") === "view" ||
            canv.getInitDataProp("boardMode") === "view_mini"
        );
    }

    isBoardModeEditMini() {
        return canv.getInitDataProp("boardMode") === "edit_mini";
    }

    isBoardModeViewCopy() {
        return canv.getInitDataProp("boardMode") === "view_copy";
    }

    isBoardModeFullRights() {
        const ifAuthorized = canv.getInitDataProp("userToken") !== ""; // ifFullRights уже гарантирует авторизованность
        const ifFullRights =
            canv.getInitDataProp("boardMode") === "edit" ||
            canv.getInitDataProp("boardMode") === "owner" ||
            canv.getInitDataProp("boardMode") === "view";
        return ifAuthorized && ifFullRights && canv.getInitDataProp("boardAccessToken") === "";
    }

    isGuest() {
        return canv.getInitDataProp("isGuest");
    }

    isTeamMember() {
        return canv.getInitDataProp("isTeamMember");
    }

    updateMinimap = rafThrottle(() => {
        eventEmitter.dispatch("minimap.update", { needUpdate: true });
    });

    updateZoomDisplay() {
        const step =
            (this.actionManager.getConstant(this.actionManager.maxZoom) -
                this.actionManager.getConstant(this.actionManager.minZoom)) /
            400;
        const normalizedZoom = Math.max(
            this.m[0],
            this.actionManager.getConstant(this.actionManager.minZoom),
        );
        const newProcent =
            (normalizedZoom - this.actionManager.getConstant(this.actionManager.minZoom)) / step;

        const ceilProcent = Math.ceil(newProcent);
        this.zoomProcent = ceilProcent;

        eventEmitter.dispatch("canvas.zoom.update", { zoom: ceilProcent });
        this.updateMinimap();
    }

    requestRedraw() {
        // Останавливаем цикл requestAnimationFrame во время экспорта PDF
        if (this.isHighPdfExporting) {
            return;
        }

        if (!this.animator.isEmpty()) {
            this.redraw();
        }

        canv.redraw(true);
        requestAnimationFrame(this.requestRedraw);
    }

    cleanUp() {
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    destroy() {
        this.cleanUp();
    }

    /**
     *
     * @param { Group[] | null } specialGroups
     * @param { boolean } skipUpdateDisplay
     * @returns { void }
     */
    zoomToSelected(specialGroups: Group[] | null = null, skipUpdateDisplay = false) {
        if (specialGroups != null && specialGroups.length === 0) {
            return;
        }

        if (specialGroups == null && canv.canvasController.selected.length === 0) {
            return;
        }

        const selectedProps = canv.canvasController.getSelectedProps(specialGroups);

        if (
            selectedProps.height === 0 ||
            selectedProps.width === 0 ||
            checkMaxNum(selectedProps.height) ||
            checkMaxNum(selectedProps.width)
        ) {
            // selectedProps["height"] == 0 приводит к this.m == infinity, так как используется в деление
            return;
        }

        if (canv.m[0] === 0 || canv.m[3] === 0) {
            // используются для деления
            return;
        }

        const screenData = {
            simulatedSelectingStartX: -canv.m[4] / canv.m[0],
            simulatedSelectingStartY: -canv.m[5] / canv.m[3],
            simulatedSelectingWidth:
                canv.canvas.width / canv.m[0] - ((2 * REAL_HTML_PADDING_X) / canv.m[0]) * this.dpr,
            simulatedSelectingHeight:
                canv.canvas.height / canv.m[3] -
                ((2 * REAL_HTML_PADDING_Y + REAL_HTML_TOP_HEIGHT) / canv.m[0]) * this.dpr,
        };

        let koef = screenData.simulatedSelectingHeight / selectedProps.height;
        let fit = "width";

        if (screenData.simulatedSelectingWidth / selectedProps.width < koef) {
            koef = screenData.simulatedSelectingWidth / selectedProps.width;
            fit = "height";
        }

        const minZoom = canv.actionManager.getConstant(canv.actionManager.minZoom);
        const normalizedKoef0 = Math.max(canv.m[0] * koef, minZoom);
        const normalizedKoef3 = Math.max(canv.m[3] * koef, minZoom);
        canv.m[0] = normalizedKoef0;
        canv.m[3] = normalizedKoef3;
        canv.m[4] = -selectedProps.startX * canv.m[0] + REAL_HTML_PADDING_X * this.dpr;
        canv.m[5] =
            -selectedProps.startY * canv.m[0] +
            (REAL_HTML_TOP_HEIGHT + REAL_HTML_PADDING_Y) * this.dpr;

        // Тут newSimulatedSelectingWidth, newSimulatedSelectingHeight это те же что и в screenData, но рассчитанные с новым canv.m[0]
        const newSimulatedSelectingWidth =
            canv.canvas.width / canv.m[0] - ((2 * REAL_HTML_PADDING_X) / canv.m[0]) * this.dpr;
        const newSimulatedSelectingHeight =
            canv.canvas.height / canv.m[3] -
            ((2 * REAL_HTML_PADDING_Y + REAL_HTML_TOP_HEIGHT) / canv.m[0]) * this.dpr;

        if (fit === "width") {
            canv.m[4] += ((newSimulatedSelectingWidth - selectedProps.width) / 2) * canv.m[0];
        } else {
            canv.m[5] += ((newSimulatedSelectingHeight - selectedProps.height) / 2) * canv.m[0];
        }

        canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
        if (!skipUpdateDisplay) {
            canv.updateZoomDisplay();
        }
    }

    zoomTo100() {
        const step =
            (canv.actionManager.getConstant(canv.actionManager.maxZoom) -
                canv.actionManager.getConstant(canv.actionManager.minZoom)) /
            400;
        const zoom = 100 * step + canv.actionManager.getConstant(canv.actionManager.minZoom);
        const zoomAlpha = (zoom - canv.m[0]) / canv.m[0];

        canv.m[0] = zoom;
        canv.m[3] = zoom;
        canv.m[4] -= (-canv.m[4] + canv.canvas.width / 2) * zoomAlpha;
        canv.m[5] -= (-canv.m[5] + canv.canvas.height / 2) * zoomAlpha;

        canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
        canv.updateZoomDisplay();
    }

    setViewportCenter(x: number, y: number) {
        const scaleX = canv.m[0];
        const scaleY = canv.m[3];

        const canvasWidth = canv.canvas.width;
        const canvasHeight = canv.canvas.height;

        canv.m[4] = canvasWidth / 2 - x * scaleX;
        canv.m[5] = canvasHeight / 2 - y * scaleY;

        canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);

        canv.updateZoomDisplay();
    }

    /**
     *
     * @param { Group[] | null } specialGroups
     * @returns { void }
     */
    zoomToComment(specialGroups: Group[] | null = null) {
        const selectedProps = canv.canvasController.getSelectedProps(specialGroups);

        selectedProps.height = 400;
        selectedProps.width = 400;

        const screenData = {
            simulatedSelectingStartX: -canv.m[4] / canv.m[0],
            simulatedSelectingStartY: -canv.m[5] / canv.m[3],
            simulatedSelectingWidth:
                canv.canvas.width / canv.m[0] - ((2 * REAL_HTML_PADDING_X) / canv.m[0]) * this.dpr,
            simulatedSelectingHeight:
                canv.canvas.height / canv.m[3] -
                ((2 * REAL_HTML_PADDING_Y + REAL_HTML_TOP_HEIGHT) / canv.m[0]) * this.dpr,
        };
        let koef = screenData.simulatedSelectingHeight / selectedProps.height;
        let fit = "width";
        if (screenData.simulatedSelectingWidth / selectedProps.width < koef) {
            koef = screenData.simulatedSelectingWidth / selectedProps.width;
            fit = "height";
        }

        canv.m[0] *= koef;
        canv.m[3] *= koef;
        canv.m[4] = -selectedProps.startX * canv.m[0] + REAL_HTML_PADDING_X * this.dpr;
        canv.m[5] =
            -selectedProps.startY * canv.m[0] +
            (REAL_HTML_TOP_HEIGHT + REAL_HTML_PADDING_Y) * this.dpr;

        // Тут newSimulatedSelectingWidth, newSimulatedSelectingHeight это те же что и в screenData, но рассчитанные с новым canv.m[0]
        const newSimulatedSelectingWidth =
            canv.canvas.width / canv.m[0] - ((2 * REAL_HTML_PADDING_X) / canv.m[0]) * this.dpr;
        const newSimulatedSelectingHeight =
            canv.canvas.height / canv.m[3] -
            ((2 * REAL_HTML_PADDING_Y + REAL_HTML_TOP_HEIGHT) / canv.m[0]) * this.dpr;
        if (fit === "width") {
            canv.m[4] += ((newSimulatedSelectingWidth - selectedProps.width) / 2) * canv.m[0];
        } else {
            canv.m[5] += ((newSimulatedSelectingHeight - selectedProps.height) / 2) * canv.m[0];
        }

        const step =
            (canv.actionManager.getConstant(canv.actionManager.maxZoom) -
                canv.actionManager.getConstant(canv.actionManager.minZoom)) /
            400;
        const zoom = 100 * step + canv.actionManager.getConstant(canv.actionManager.minZoom);
        const zoomAlpha = (zoom - canv.m[0]) / canv.m[0];

        canv.m[0] = zoom;
        canv.m[3] = zoom;
        canv.m[4] -= (-canv.m[4] + canv.canvas.width / 2) * zoomAlpha;
        canv.m[5] -= (-canv.m[5] + canv.canvas.height / 2) * zoomAlpha;

        canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
        canv.updateZoomDisplay();
    }

    /**
     *
     * @param { Group[] } specialGroups
     * @returns { void }
     */
    zoomToVoteResultItem(specialGroups: Group) {
        const selectedProps = {
            height: 400,
            width: 400,
            startX: specialGroups.startX,
            startY: specialGroups.startY,
        };

        const screenData = {
            simulatedSelectingStartX: -canv.m[4] / canv.m[0],
            simulatedSelectingStartY: -canv.m[5] / canv.m[3],
            simulatedSelectingWidth:
                canv.canvas.width / canv.m[0] - ((2 * REAL_HTML_PADDING_X) / canv.m[0]) * this.dpr,
            simulatedSelectingHeight:
                canv.canvas.height / canv.m[3] -
                ((2 * REAL_HTML_PADDING_Y + REAL_HTML_TOP_HEIGHT) / canv.m[0]) * this.dpr,
        };
        let koef = screenData.simulatedSelectingHeight / selectedProps.height;
        let fit = "width";
        if (screenData.simulatedSelectingWidth / selectedProps.width < koef) {
            koef = screenData.simulatedSelectingWidth / selectedProps.width;
            fit = "height";
        }

        canv.m[0] *= koef;
        canv.m[3] *= koef;
        canv.m[4] = -selectedProps.startX * canv.m[0] + REAL_HTML_PADDING_X * this.dpr;
        canv.m[5] =
            -selectedProps.startY * canv.m[0] +
            (REAL_HTML_TOP_HEIGHT + REAL_HTML_PADDING_Y) * this.dpr;

        // Тут newSimulatedSelectingWidth, newSimulatedSelectingHeight это те же что и в screenData, но рассчитанные с новым canv.m[0]
        const newSimulatedSelectingWidth =
            canv.canvas.width / canv.m[0] - ((2 * REAL_HTML_PADDING_X) / canv.m[0]) * this.dpr;
        const newSimulatedSelectingHeight =
            canv.canvas.height / canv.m[3] -
            ((2 * REAL_HTML_PADDING_Y + REAL_HTML_TOP_HEIGHT) / canv.m[0]) * this.dpr;
        if (fit === "width") {
            canv.m[4] += ((newSimulatedSelectingWidth - selectedProps.width) / 2) * canv.m[0];
        } else {
            canv.m[5] += ((newSimulatedSelectingHeight - selectedProps.height) / 2) * canv.m[0];
        }

        const step =
            (canv.actionManager.getConstant(canv.actionManager.maxZoom) -
                canv.actionManager.getConstant(canv.actionManager.minZoom)) /
            400;
        const zoom = 100 * step + canv.actionManager.getConstant(canv.actionManager.minZoom);
        const zoomAlpha = (zoom - canv.m[0]) / canv.m[0];

        canv.m[0] = zoom;
        canv.m[3] = zoom;
        canv.m[4] -= (-canv.m[4] + canv.canvas.width / 2) * zoomAlpha;
        canv.m[5] -= (-canv.m[5] + canv.canvas.height / 2) * zoomAlpha;

        canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
        canv.updateZoomDisplay();
        canv.redraw();
    }

    zoomToVoteCandidates(specialGroups: Group[]) {
        if (!specialGroups) return;

        const selectedProps = canv.canvasController.getSelectedProps(specialGroups);
        selectedProps.width = selectedProps.width + getScaledSize(400);

        const screenData = {
            simulatedSelectingStartX: -canv.m[4] / canv.m[0],
            simulatedSelectingStartY: -canv.m[5] / canv.m[3],
            simulatedSelectingWidth:
                canv.canvas.width / canv.m[0] - ((2 * REAL_HTML_PADDING_X) / canv.m[0]) * canv.dpr,
            simulatedSelectingHeight:
                canv.canvas.height / canv.m[3] -
                ((2 * REAL_HTML_PADDING_Y + REAL_HTML_TOP_HEIGHT) / canv.m[0]) * canv.dpr,
        };
        let koef = screenData.simulatedSelectingHeight / selectedProps.height;
        let fit = "width";

        if (screenData.simulatedSelectingWidth / selectedProps.width < koef) {
            koef = screenData.simulatedSelectingWidth / selectedProps.width;
            fit = "height";
        }

        const minZoom = canv.actionManager.getConstant(canv.actionManager.minZoom);
        const normalizedKoef0 = Math.max(canv.m[0] * koef, minZoom);
        const normalizedKoef3 = Math.max(canv.m[3] * koef, minZoom);
        canv.m[0] = normalizedKoef0;
        canv.m[3] = normalizedKoef3;
        canv.m[4] = -selectedProps.startX * canv.m[0] + REAL_HTML_PADDING_X * canv.dpr;
        canv.m[5] =
            -selectedProps.startY * canv.m[0] +
            (REAL_HTML_TOP_HEIGHT + REAL_HTML_PADDING_Y) * canv.dpr;

        // Тут newSimulatedSelectingWidth, newSimulatedSelectingHeight это те же что и в screenData, но рассчитанные с новым canv.m[0]
        const newSimulatedSelectingWidth =
            canv.canvas.width / canv.m[0] - ((2 * REAL_HTML_PADDING_X) / canv.m[0]) * canv.dpr;
        const newSimulatedSelectingHeight =
            canv.canvas.height / canv.m[3] -
            ((2 * REAL_HTML_PADDING_Y + REAL_HTML_TOP_HEIGHT) / canv.m[0]) * canv.dpr;
        if (fit === "width") {
            canv.m[4] += ((newSimulatedSelectingWidth - selectedProps.width) / 2) * canv.m[0];
        } else {
            canv.m[5] += ((newSimulatedSelectingHeight - selectedProps.height) / 2) * canv.m[0];
        }

        canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
        canv.updateZoomDisplay();
    }

    getZoomToTransformations(selectedProps: Pick<Group, "startX" | "startY" | "width" | "height">) {
        const screenData = {
            simulatedSelectingStartX: -canv.m[4] / canv.m[0],
            simulatedSelectingStartY: -canv.m[5] / canv.m[3],
            simulatedSelectingWidth:
                canv.canvas.width / canv.m[0] - ((2 * REAL_HTML_PADDING_X) / canv.m[0]) * this.dpr,
            simulatedSelectingHeight:
                canv.canvas.height / canv.m[3] -
                ((2 * REAL_HTML_PADDING_Y + REAL_HTML_TOP_HEIGHT) / canv.m[0]) * this.dpr,
        };
        let koef = screenData.simulatedSelectingHeight / selectedProps.height;
        let fit = "width";

        if (screenData.simulatedSelectingWidth / selectedProps.width < koef) {
            koef = screenData.simulatedSelectingWidth / selectedProps.width;
            fit = "height";
        }

        const result = {
            0: canv.m[0] * koef,
            3: canv.m[3] * koef,
            4: -selectedProps.startX * canv.m[0] * koef + REAL_HTML_PADDING_X * this.dpr,
            5:
                -selectedProps.startY * canv.m[0] * koef +
                (REAL_HTML_TOP_HEIGHT + REAL_HTML_PADDING_Y) * this.dpr,
        };

        // Тут newSimulatedSelectingWidth, newSimulatedSelectingHeight это те же что и в screenData, но рассчитанные с новым canv.m[0]
        const newSimulatedSelectingWidth =
            canv.canvas.width / result["0"] - ((2 * REAL_HTML_PADDING_X) / result["0"]) * this.dpr;
        const newSimulatedSelectingHeight =
            canv.canvas.height / result["3"] -
            ((2 * REAL_HTML_PADDING_Y + REAL_HTML_TOP_HEIGHT) / result["0"]) * this.dpr;
        if (fit === "width") {
            result["4"] += ((newSimulatedSelectingWidth - selectedProps.width) / 2) * result["0"];
        } else {
            result["5"] += ((newSimulatedSelectingHeight - selectedProps.height) / 2) * result["0"];
        }

        return result;
    }

    checkLinkToWidget() {
        const params = new URL(window.location.href).searchParams;
        const linkToWidget = params.get("linkToWidget");

        if (linkToWidget) {
            const found = canv.canvasController.FindByUID(linkToWidget);

            if (!found) return;

            if (found instanceof CommentWidget) {
                canv.zoomToComment([found.parent]);
                found.TryAction("mouseup");
            } else {
                canv.zoomToSelected([found] as Group[]);
            }
        }
    }

    /**
     *
     * @param {boolean} exec
     * @param {boolean} needBack
     * @param {Group[] | undefined} groupsList
     * @returns {void}
     */
    redraw(exec = false, needBack = false, groupsList: Group[] | undefined = undefined) {
        if (!exec) {
            this.needRedraw = true;
            return;
        }

        if (!this.needRedraw) {
            return;
        }

        this.needRedraw = false;
        canv.ctx.save();
        canv.ctx.setTransform(1, 0, 0, 1, 0, 0);
        canv.ctx.clearRect(0, 0, canv.canvas.width, canv.canvas.height);
        canv.ctx.restore();

        if (needBack) {
            const screenData = {
                simulatedSelectingStartX: -canv.m[4] / canv.m[0],
                simulatedSelectingStartY: -canv.m[5] / canv.m[3],
                simulatedSelectingWidth: canv.canvas.width / canv.m[0],
                simulatedSelectingHeight: canv.canvas.height / canv.m[3],
            };

            canv.ctx.fillStyle = "#F9F9F9";
            this.ctx.rect(
                screenData.simulatedSelectingStartX,
                screenData.simulatedSelectingStartY,
                screenData.simulatedSelectingWidth,
                screenData.simulatedSelectingHeight,
            );
            canv.ctx.fill();
        }

        if (
            canv.appInterface.voteWindow.opened &&
            canv.appInterface.voteWindow.activeTab === "new"
        ) {
            canv.appInterface.voteWindow.screen?.draw();
        }

        if (canv.appInterface.imageExportWindow.opened && groupsList === undefined) {
            canv.appInterface.imageExportWindow.screen.draw();
        }

        this.drawGrid();

        let container = this.canvasController.items.concat(
            Array.from(this.canvasController.groups.values()),
        );

        if (groupsList !== undefined) {
            container = groupsList;
        }

        for (let i = 0; i < container.length; i++) {
            container[i].draw(groupsList !== undefined);
        }

        searchController.draw();

        const elementsToDrawOverOver: Group[] = [];

        for (let i = 0; i < container.length; i++) {
            if (container[i]?.onlyChild()?.constructorName === "comment") {
                elementsToDrawOverOver.push(container[i]);
            } else {
                container[i].drawOver();
            }
        }

        elementsToDrawOverOver.forEach((container) => {
            container.drawOver();
        });

        if (
            canv.appInterface.voteWindow.opened &&
            canv.appInterface.voteWindow.activeTab === "new"
        ) {
            canv.appInterface.voteWindow.screen?.drawBackground();
            canv.appInterface.voteWindow.screen?.drawCorner();
            canv.appInterface.voteWindow.screen?.drawTooltip();
        }

        if (canv.appInterface.imageExportWindow.opened && groupsList === undefined) {
            canv.appInterface.imageExportWindow.screen.drawBackground();
            canv.appInterface.imageExportWindow.screen.drawCorner();
            canv.appInterface.imageExportWindow.screen.drawTooltip();
        }

        this.animator.frame();

        canv.toolBox.drawTool();
        this.multiManager.draw();
    }

    gridGap = 50;
    gridRadius = 1;

    drawGrid() {
        const size = 16;
        const viewport = getViewport();
        const ratio = this.canvas.width / viewport.viewportWidth;
        const minSizeByPx = 32;
        const minSize = minSizeByPx * (viewport.viewportWidth / this.canvas.width);

        let calcedGridGap = size;
        while (calcedGridGap < minSize) {
            calcedGridGap *= 4;
        }

        const lineWidth = 1 / ratio;

        const startX = viewport.viewportStartX;
        const startY = viewport.viewportStartY;
        const endX = viewport.viewportStartX + viewport.viewportWidth;
        const endY = viewport.viewportStartY + viewport.viewportHeight;
        const defaultColor = "rgba(240, 240, 243, 1)";
        const borderColor = "rgba(232, 232, 238, 1)";

        const firstXLine =
            startX > 0 ? Math.ceil(startX / calcedGridGap) : Math.floor(startX / calcedGridGap);
        const firstYLine =
            startY > 0 ? Math.ceil(startY / calcedGridGap) : Math.floor(startY / calcedGridGap);
        const lastXLine =
            endX > 0 ? Math.ceil(endX / calcedGridGap) : Math.floor(endX / calcedGridGap);
        const lastYLine =
            endY > 0 ? Math.ceil(endY / calcedGridGap) : Math.floor(endY / calcedGridGap);

        canv.ctx.strokeStyle = defaultColor;
        canv.ctx.lineWidth = lineWidth;

        for (let y = firstYLine; y <= lastYLine; y++) {
            canv.ctx.strokeStyle = defaultColor;

            if (y % 4 === 0) {
                canv.ctx.strokeStyle = borderColor;
            }

            canv.ctx.beginPath();
            canv.ctx.moveTo(startX, y * calcedGridGap);
            canv.ctx.lineTo(endX, y * calcedGridGap);
            canv.ctx.stroke();
        }

        for (let x = firstXLine; x <= lastXLine; x++) {
            canv.ctx.strokeStyle = defaultColor;

            if (x % 4 === 0) {
                canv.ctx.strokeStyle = borderColor;
            }

            canv.ctx.beginPath();
            canv.ctx.moveTo(x * calcedGridGap, startY);
            canv.ctx.lineTo(x * calcedGridGap, endY);
            canv.ctx.stroke();
        }
    }

    setIsHighPdfExporting(isExporting = false) {
        this.isHighPdfExporting = isExporting;

        if (!isExporting) {
            // Возобновляем цикл requestAnimationFrame после экспорта PDF
            window.requestAnimationFrame(this.requestRedraw);
        }
    }

    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
    progressBar: ProgressBar;
    toolBox: toolBox;
    keyboard: KeyboardHandler3;
    contextMenu: ContextMenuController;
    canvasController: canvasController;
    appInterface: appInterface;
    intercom: Intercom;
    multiManager: multiManager;
    animator: Animator;
    mediator: Mediator;
    widgetMenu: widgetMenu;
    actionManager: actionManager;
    eventManager: EventManager;
    shapeStorage: ShapeStorage;
    commentsPopovers: CommentsPopovers;
    textEditingController: TextEditingController;
    reserveWsHost: string;
    dpr: number = 2;
    pingTimer: NodeJS.Timeout | number | undefined;
    podAlias = "1";
    initData: InitialData | undefined;
    sessionUID = "";
    reservePodAlias = "1";
    needRedraw = false;
    cursorTypeByEntity = "";
    m = [2, 0, 0, 2, 0, 0];
    host = "";
    wsHost = "";
    reserveWsDomain = "";
    isReconnectionProcess = false;
    isPresentationMode = false;
    highloadMode = false;
    animations = [];
    isHighPdfExporting = false;
    zoomProcent = 0;
    hoverArea: HoverArea | null = null;
    rafId = null;
}
